import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";

/**
 * Header bell — polls /whiteboard/unread-count every 60s, shows an unread
 * badge, and turns red when there's an unread EMERGENCY announcement.
 * Also refetches on:
 *   - route change (so opening/leaving /announcements updates the badge),
 *   - the custom `whiteboard-read` window event (fired by WhiteboardPage
 *     after it marks visible posts as read).
 *
 * Kept intentionally compact so it fits inside the top-bar right cluster
 * without pushing anything off-screen (see Layout.js top-bar overflow fix).
 */
export default function AnnouncementBell() {
    const [count, setCount] = useState(0);
    const [emergCount, setEmergCount] = useState(0);
    const nav = useNavigate();
    const location = useLocation();

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/whiteboard/unread-count");
            setCount(data?.unread || 0);
            setEmergCount(data?.emergency_unread || 0);
        } catch {
            // Fail silent — bell never blocks the header.
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);
        const onRead = () => load();
        window.addEventListener("whiteboard-read", onRead);
        return () => {
            clearInterval(t);
            window.removeEventListener("whiteboard-read", onRead);
        };
    }, [load]);

    // Refetch when the user navigates (e.g. arriving on /announcements will
    // trigger the mark-read effect, and leaving it should immediately reflect
    // the new count without waiting for the 60s poll).
    useEffect(() => { load(); }, [location.pathname, load]);

    const hasUnread = count > 0;
    const hasEmergency = emergCount > 0;
    const label = hasEmergency
        ? `${emergCount} emergency announcement${emergCount === 1 ? "" : "s"} unread`
        : hasUnread
            ? `${count} announcement${count === 1 ? "" : "s"} unread`
            : "No new announcements";

    return (
        <button
            onClick={() => nav("/announcements")}
            className="relative p-2 rounded-md text-white/85 hover:text-white hover:bg-white/10"
            data-testid="header-announcement-bell"
            data-unread={hasUnread ? "true" : "false"}
            data-emergency-unread={hasEmergency ? "true" : "false"}
            aria-label={label}
            title={label}
        >
            <Bell className="w-4 h-4" />
            {hasUnread && (
                <span
                    className={`absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${hasEmergency ? "bg-[#E02B2B]" : "bg-white/85 !text-[#1E3355]"}`}
                    data-testid="header-announcement-bell-badge"
                >
                    {count > 99 ? "99+" : count}
                </span>
            )}
        </button>
    );
}
